import { useState, type FormEvent } from 'react';
import { ConfirmButton } from '../components/confirm';
import { Field } from '../components/page';
import { ActionError } from '../components/states';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox, Input, Select } from '../components/ui/input';
import { useApi } from '../lib/auth';
import { money, parseDinars, toDinarsInput } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useApiMutation } from '../lib/query';
import type { InstallmentDetail, PaymentResult } from '../lib/types';

// Actions de la fiche échéance (BA-035, spec 08 §2) : paiement hors plateforme, envoi
// immédiat de l'étape en cours.

export function InstallmentActionsCard({
  installment,
  onDone,
  paymentOnly = false,
}: {
  installment: InstallmentDetail;
  onDone: () => void;
  /** Agent : encaissement seulement, sans lien de paiement ni envoi (BA-073). */
  paymentOnly?: boolean;
}) {
  const { t, language } = useI18n();
  const api = useApi();
  const remaining = Math.max(0, installment.amountDue - installment.amountPaid);
  const [form, setForm] = useState({
    amount: remaining > 0 ? toDinarsInput(remaining) : '',
    method: 'agency',
    paidAt: '',
    reference: '',
    issueReceipt: true,
  });
  const [result, setResult] = useState<PaymentResult | null>(null);
  const record = useApiMutation<Record<string, unknown>, PaymentResult>(
    'POST',
    `/v1/installments/${installment.id}/payments`,
    {
      invalidate: ['/v1/installments', '/v1/dashboard', '/v1/escalations'],
      onSuccess: (r) => {
        setResult(r);
        onDone();
      },
    },
  );
  const [payLink, setPayLink] = useState<string | null>(null);
  const amount = parseDinars(form.amount);
  const settled = ['paid', 'cancelled', 'waived'].includes(installment.status);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!amount || !form.paidAt) return;
    record.mutate({
      amount,
      method: form.method,
      paidAt: form.paidAt,
      ...(form.reference.trim() ? { reference: form.reference.trim() } : {}),
      issueReceipt: form.issueReceipt,
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.payments.record}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {!settled ? (
          <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2" noValidate>
            <Field
              label={t.payments.amount}
              error={form.amount && !amount ? t.common.required : undefined}
            >
              <Input
                inputMode="decimal"
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              />
            </Field>
            <Field label={t.payments.method}>
              <Select
                value={form.method}
                onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))}
              >
                {(['agency', 'bank', 'check', 'other'] as const).map((m) => (
                  <option key={m} value={m}>
                    {t.paymentMethods[m]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t.payments.paidAt}>
              <Input
                type="date"
                value={form.paidAt}
                onChange={(e) => setForm((f) => ({ ...f, paidAt: e.target.value }))}
              />
            </Field>
            <Field label={t.payments.reference}>
              <Input
                value={form.reference}
                onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
              />
            </Field>
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <Checkbox
                checked={form.issueReceipt}
                onChange={(e) => setForm((f) => ({ ...f, issueReceipt: e.target.checked }))}
              />
              {t.payments.issueReceipt}
            </label>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={!amount || !form.paidAt || record.isPending}>
                {t.payments.record}
              </Button>
            </div>
          </form>
        ) : null}
        {result ? (
          <p role="status" className="text-sm text-success">
            {t.payments.recorded}
            {result.overpaid > 0
              ? ` ${t.payments.overpaid} (${money(result.overpaid, language)}).`
              : ''}
          </p>
        ) : null}
        <ActionError error={record.error} />
        {paymentOnly ? null : payLink ? (
          <Field label={t.payments.payLink}>
            <Input readOnly dir="ltr" value={payLink} onFocus={(e) => e.target.select()} />
          </Field>
        ) : (
          <div>
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                void api
                  .get<{ url: string }>(`/v1/installments/${installment.id}/pay-link`)
                  .then((r) => setPayLink(r.url))
              }
            >
              {t.payments.showPayLink}
            </Button>
          </div>
        )}
        {!paymentOnly &&
        installment.sequence &&
        installment.sequence.status !== 'running' &&
        !settled ? (
          <ConfirmButton
            label={t.payments.sendNow}
            title={t.payments.sendNow}
            message={t.payments.sendNowConfirm}
            onConfirm={async () => {
              await api.request('POST', `/v1/installments/${installment.id}/send-now`);
              onDone();
            }}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}
